/**
 * Lectura y escritura del `.canvas`, el formato de archivo de Blanqo.
 *
 * El formato NACIÓ como JSON Canvas (https://jsoncanvas.org) y conserva su
 * forma —`nodes` / `edges`, rects en coordenadas de mundo— porque es un buen
 * esquema y porque los archivos que ya existen están escritos así. Pero la
 * spec dejó de ser un contrato: **el `.canvas` es de Blanqo y evoluciona con
 * Blanqo**. Se le agregan tipos de nodo y campos propios cuando el producto
 * los necesita, sin recortarlos para que otra aplicación los entienda.
 *
 * El compromiso de portabilidad está en otro lado y sigue firme: los `.md` del
 * vault se escriben como markdown plano, legible y editable en cualquier otro
 * editor (ver `edit/formatting.ts` y `edit/livePreview.ts`). El `.md` es del
 * usuario; el `.canvas` es la capa espacial de Blanqo.
 *
 * El viewport no se guarda acá: es estado de sesión y vive aparte, por path
 * (ver `vault/fsAccess.ts`).
 */

import {
  type Card,
  type CardType,
  type CanvasDoc,
  type CommentData,
  type Edge,
  type EdgeEnd,
  type EdgeRoute,
  type EdgeSide,
  type ShapeData,
  type TextData,
  DEFAULT_FROM_END,
  DEFAULT_TO_END,
  DEFAULT_ROUTE,
  createId,
  nextGroupName,
  normalizeShape,
  normalizeText,
  TEXT_BG_NONE,
  TEXT_COLOR_DEFAULT,
} from "../model/types.ts";

/** Versión del formato que escribe esta build. Sólo se emite; todavía no hay
 *  ninguna lógica colgada de ella. Es lo que le va a permitir a una versión
 *  futura saber qué está leyendo antes de reescribir el archivo. */
const FORMAT_VERSION = 1;

const SIDES = new Set<string>(["top", "right", "bottom", "left"]);
const ENDS = new Set<string>(["none", "arrow"]);
const ROUTES = new Set<string>(["elbow", "straight", "curved"]);

/** Devuelve el valor sólo si es uno de los que el tipo admite. Un archivo
 *  editado a mano no debería poder meter un `fromSide: "arriba"` al modelo. */
function oneOf<T extends string>(value: unknown, allowed: Set<string>): T | undefined {
  return typeof value === "string" && allowed.has(value) ? (value as T) : undefined;
}

/** Un nodo del archivo. Hoy se emiten `text`, `file`, `shape` y `group`. */
interface JSONCanvasNode {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** `text` nodes. */
  text?: string;
  /** `file` nodes. */
  file?: string;
  /** `group` nodes: etiqueta visible del grupo (campo del spec JSON Canvas). */
  label?: string;
  color?: string;
  /**
   * `shape` nodes: geometría y relleno. Campo propio de Blanqo. El texto de la
   * forma NO va acá sino en `text`, como el de cualquier otro nodo, así que un
   * archivo viejo se sigue leyendo sin tocarlo.
   *
   * `stroke` y `strokeWidth` son de la versión anterior, cuando el relleno era
   * un tinte casi blanco y el color elegido iba al borde. No se escriben más;
   * se leen sólo para migrar (ver `normalizeShape`).
   */
  shape?: {
    kind?: string;
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    /** Alineación del texto adentro de la figura. Como en `textStyle`, sólo se
     *  escribe cuando NO es la de por defecto (centrada). */
    align?: string;
  };
  /**
   * `text` nodes: alineación y fondo. Campo propio de Blanqo, y opcional: el
   * texto en sí sigue yendo en `text`, como el de cualquier otro nodo, así que
   * un archivo escrito antes de que el texto tuviera estilo se lee sin tocarlo
   * y uno escrito hoy se sigue leyendo en cualquier otra app de JSON Canvas.
   *
   * Sólo se escriben los valores que NO son el de por defecto, para que el
   * archivo quede legible y mover una tarjeta no arrastre ruido al diff.
   */
  textStyle?: {
    align?: string;
    background?: string;
    color?: string;
    autoWidth?: boolean;
  };
  /** `comment` nodes: comportamiento de la burbuja anclada. */
  commentStyle?: {
    pinned?: boolean;
    bodyAutoScale?: boolean;
  };
  /**
   * `group` nodes: cómo se comporta el título. Campo propio de Blanqo y, como
   * los otros `*Style`, sólo se escribe cuando NO es el valor por defecto: un
   * archivo sin él se lee igual, y otra app de JSON Canvas lo ignora.
   */
  groupStyle?: {
    titleAutoScale?: boolean;
  };
}

interface JSONCanvasEdge {
  id: string;
  fromNode: string;
  toNode: string;
  fromSide?: string;
  toSide?: string;
  fromEnd?: string;
  toEnd?: string;
  color?: string;
  label?: string;
  /** Campo propio de Blanqo: forma de la ruta. */
  route?: string;
}

/**
 * A persisted pencil stroke (an `ink` card). `points` are normalized (0..1)
 * to the x/y/width/height rect; `size` is the stroke width in world px at
 * that rect size. Legacy files (first ink version) stored absolute world
 * points and no rect — the parser detects that by the missing `x`.
 */
interface JSONCanvasStroke {
  id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  color?: string;
  size?: number;
  points: number[];
}

export interface JSONCanvas {
  /** Ver `FORMAT_VERSION`. Ausente en los archivos escritos antes de existir. */
  version?: number;
  nodes: JSONCanvasNode[];
  edges: JSONCanvasEdge[];
  /**
   * Trazos a mano alzada. Campo propio de Blanqo al tope del archivo, fuera
   * del esquema original de JSON Canvas. Sólo se emite si el documento tiene
   * tinta.
   */
  strokes?: JSONCanvasStroke[];
}

/** Serialize a CanvasDoc into `.canvas` text. */
export function toJSONCanvas(doc: CanvasDoc): string {
  const nodes: JSONCanvasNode[] = doc.cards
    .filter((c) => c.type !== "ink")
    .map((c) => {
      const node: JSONCanvasNode = {
        id: c.id,
        type: c.type,
        x: Math.round(c.x),
        y: Math.round(c.y),
        width: Math.round(c.width),
        height: Math.round(c.height),
      };
      if (c.type === "file") node.file = c.file ?? "";
      else if (c.type === "shape") {
        // El `?? c.markdown` no es paranoia de más: es de dónde venía el texto
        // ANTES de que la forma y el nodo de texto tuvieran campo propio. Si por
        // el motivo que sea llega acá una tarjeta sin normalizar (un módulo
        // recargado en caliente sobre un documento ya cargado, un camino nuevo
        // que se saltee `normalizeDoc`), sin este respaldo el guardado escribe
        // "" ENCIMA del archivo y el texto se pierde para siempre. Escribir de
        // más es recuperable; pisar con vacío no.
        node.text = c.shape?.text ?? c.markdown ?? "";
        node.shape = { kind: c.shape?.kind ?? "rect", fill: c.shape?.fill };
        if (c.shape && c.shape.align !== "center") node.shape.align = c.shape.align;
      } else if (c.type === "text") {
        node.text = c.text?.text ?? c.markdown ?? "";
        const style: NonNullable<JSONCanvasNode["textStyle"]> = {};
        if (c.text && c.text.align !== "left") style.align = c.text.align;
        if (c.text && c.text.background !== TEXT_BG_NONE) style.background = c.text.background;
        if (c.text && c.text.color !== TEXT_COLOR_DEFAULT) style.color = c.text.color;
        if (c.text?.autoWidth) style.autoWidth = true;
        if (Object.keys(style).length > 0) node.textStyle = style;
      } else if (c.type === "comment") {
        node.text = c.comment?.text ?? "";
        const style: NonNullable<JSONCanvasNode["commentStyle"]> = {};
        if (c.comment?.pinned) style.pinned = true;
        if (c.comment?.bodyAutoScale) style.bodyAutoScale = true;
        if (Object.keys(style).length > 0) node.commentStyle = style;
      } else if (c.type === "group") {
        node.text = c.childrenIds?.join(",") ?? "";
        if (c.name) node.label = c.name;
        node.color = c.color;
        if (c.titleAutoScale) node.groupStyle = { titleAutoScale: true };
      } else node.text = c.markdown;
      return node;
    });

  // Los valores por defecto no se escriben: el archivo queda legible y el diff
  // de mover una tarjeta no arrastra ruido.
  const edges: JSONCanvasEdge[] = doc.edges.map((e) => {
    const edge: JSONCanvasEdge = { id: e.id, fromNode: e.fromNode, toNode: e.toNode };
    if (e.fromSide) edge.fromSide = e.fromSide;
    if (e.toSide) edge.toSide = e.toSide;
    if (e.fromEnd && e.fromEnd !== DEFAULT_FROM_END) edge.fromEnd = e.fromEnd;
    if (e.toEnd && e.toEnd !== DEFAULT_TO_END) edge.toEnd = e.toEnd;
    if (e.color) edge.color = e.color;
    if (e.label) edge.label = e.label;
    if (e.route && e.route !== DEFAULT_ROUTE) edge.route = e.route;
    return edge;
  });

  const inkCards = doc.cards.filter((c) => c.type === "ink" && c.ink);
  const out: JSONCanvas = { version: FORMAT_VERSION, nodes, edges };
  if (inkCards.length > 0) {
    out.strokes = inkCards.map((c) => {
      const ink = c.ink!;
      // Bake the natural-size ratio into `size` so the file doesn't need the
      // natural dimensions: on load, natural = the persisted rect.
      const scale = (c.width / ink.naturalWidth + c.height / ink.naturalHeight) / 2;
      return {
        id: c.id,
        x: Math.round(c.x * 10) / 10,
        y: Math.round(c.y * 10) / 10,
        width: Math.round(c.width * 10) / 10,
        height: Math.round(c.height * 10) / 10,
        color: ink.color,
        size: Math.round(ink.size * scale * 100) / 100,
        points: ink.points.map((v) => Math.round(v * 10000) / 10000),
      };
    });
  }
  return JSON.stringify(out, null, 2);
}

/**
 * La forma de un nodo `shape`. La normalización (kinds que ya no existen,
 * relleno que vivía en el borde) es la misma que para un documento venido de
 * IndexedDB, así que la hace `normalizeShape` y no se escribe dos veces.
 */
function parseShape(raw: JSONCanvasNode["shape"], text: string | undefined): ShapeData {
  return normalizeShape(raw, text ?? "");
}

/**
 * El estilo de un nodo `text`. Como con las formas, la normalización es la
 * misma que para un documento venido de IndexedDB (`normalizeText`), así que
 * un archivo sin `textStyle` —o con uno escrito antes de que hubiera color—
 * entra con los valores por defecto y su texto viene de `text`.
 */
function parseText(raw: JSONCanvasNode["textStyle"], text: string | undefined): TextData {
  return normalizeText(raw, text ?? "");
}

function parseComment(
  raw: JSONCanvasNode["commentStyle"],
  text: string | undefined,
): CommentData {
  return {
    text: text ?? "",
    pinned: raw?.pinned === true,
    bodyAutoScale: raw?.bodyAutoScale === true,
  };
}

/**
 * Parse `.canvas` text into a CanvasDoc. Los tipos de nodo que todavía no se
 * modelan (`link`) se descartan. `file` nodes keep an empty markdown cache to
 * be filled in lazily by the render layer. The camera defaults to origin (callers typically
 * `fitAll()` or restore a saved viewport afterwards).
 */
export function fromJSONCanvas(id: string, text: string): CanvasDoc {
  let parsed: Partial<JSONCanvas>;
  try {
    parsed = text.trim() ? (JSON.parse(text) as Partial<JSONCanvas>) : {};
  } catch {
    parsed = {};
  }

  const now = Date.now();
  const cards: Card[] = (parsed.nodes ?? [])
    .filter(
      (n) =>
        n.type === "text" ||
        n.type === "file" ||
        n.type === "group" ||
        n.type === "shape" ||
        n.type === "comment",
    )
    .map((n) => {
      const type = n.type as CardType;
      const isGroup = type === "group";
      const isShape = type === "shape";
      const isText = type === "text";
      const isComment = type === "comment";
      return {
        id: n.id || createId(),
        type,
        x: n.x ?? 0,
        y: n.y ?? 0,
        width: n.width ?? 280,
        height: n.height ?? 180,
        // Ni una forma ni un texto usan `markdown`: cada uno tiene su campo.
        markdown: "",
        file: type === "file" ? n.file ?? "" : undefined,
        shape: isShape ? parseShape(n.shape, n.text) : undefined,
        text: isText ? parseText(n.textStyle, n.text) : undefined,
        comment: isComment ? parseComment(n.commentStyle, n.text) : undefined,
        childrenIds: isGroup && n.text ? n.text.split(",").filter(Boolean) : undefined,
        name: isGroup ? n.label || undefined : undefined,
        color: isGroup ? n.color : undefined,
        titleAutoScale:
          isGroup && n.groupStyle?.titleAutoScale === true ? true : undefined,
        updatedAt: now,
      };
    });

  // Un .canvas escrito por otra app (o por una versión previa) puede traer
  // grupos sin `label`; se les da nombre acá para que ninguno quede sin etiqueta.
  for (const c of cards) {
    if (c.type === "group" && !c.name) c.name = nextGroupName(cards);
  }

  const inkCards: Card[] = (parsed.strokes ?? [])
    .filter((s) => Array.isArray(s.points) && s.points.length >= 2)
    .map((s) => {
      const size = s.size ?? 3;
      let rect: { x: number; y: number; width: number; height: number };
      let points: number[];
      if (typeof s.x === "number" && typeof s.width === "number") {
        rect = {
          x: s.x,
          y: s.y ?? 0,
          width: Math.max(s.width, 1),
          height: Math.max(s.height ?? 1, 1),
        };
        points = s.points;
      } else {
        // Legacy ink (v1): absolute world points, no rect. Derive the rect
        // from the stroke's bounding box and normalize, mirroring how new
        // strokes are committed.
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (let i = 0; i < s.points.length; i += 2) {
          minX = Math.min(minX, s.points[i]);
          maxX = Math.max(maxX, s.points[i]);
          minY = Math.min(minY, s.points[i + 1]);
          maxY = Math.max(maxY, s.points[i + 1]);
        }
        const pad = size / 2;
        rect = {
          x: minX - pad,
          y: minY - pad,
          width: maxX - minX + pad * 2,
          height: maxY - minY + pad * 2,
        };
        points = s.points.map((v, i) =>
          i % 2 === 0 ? (v - rect.x) / rect.width : (v - rect.y) / rect.height,
        );
      }
      return {
        id: s.id || createId("stroke"),
        type: "ink" as const,
        ...rect,
        markdown: "",
        ink: {
          color: s.color || "#111111",
          size,
          points,
          naturalWidth: rect.width,
          naturalHeight: rect.height,
        },
        updatedAt: now,
      };
    });

  const allCards = [...cards, ...inkCards];

  // Una arista sólo entra al modelo si sus dos extremos existen de verdad. Una
  // huérfana no se puede dibujar (no hay rect del que sacar la ruta), así que
  // se descarta acá y no vuelve a escribirse.
  const nodeIds = new Set(allCards.map((c) => c.id));
  const edges: Edge[] = (parsed.edges ?? [])
    .filter(
      (e) =>
        e &&
        typeof e.fromNode === "string" &&
        typeof e.toNode === "string" &&
        e.fromNode !== e.toNode &&
        nodeIds.has(e.fromNode) &&
        nodeIds.has(e.toNode),
    )
    .map((e) => ({
      id: e.id || createId("edge"),
      fromNode: e.fromNode,
      toNode: e.toNode,
      fromSide: oneOf<EdgeSide>(e.fromSide, SIDES),
      toSide: oneOf<EdgeSide>(e.toSide, SIDES),
      fromEnd: oneOf<EdgeEnd>(e.fromEnd, ENDS),
      toEnd: oneOf<EdgeEnd>(e.toEnd, ENDS),
      color: typeof e.color === "string" ? e.color : undefined,
      label: typeof e.label === "string" ? e.label : undefined,
      route: oneOf<EdgeRoute>(e.route, ROUTES),
      updatedAt: now,
    }));

  return { id, cards: allCards, edges, camera: { x: 0, y: 0, zoom: 1 } };
}
